import { isEmpty } from 'lodash';
import { useContext, useMemo } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { DropTarget } from '@shared/components/DropTarget';
import { FileList } from '@shared/components/FileList';
import { ImageList } from '@shared/components/ImageList';
import { Styles as ButtonStyles } from '@shared/components/MBButton';
import { View } from '@shared/components/View';
import { FileUpload } from '@shared/components/blocks/types';
import { MBFileDropListener } from '@shared/components/form/fileDropListener';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBTouchableOverlay } from '@shared/components/touchable';
import { MBTextBody1Bold } from '@shared/components/typography';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { ColorThemeContext, grid } from '@shared/styles';
import { isImage } from '@shared/util/media/type';
import { UseFileUploadProps } from './useFileUpload';

interface Props extends UseFileUploadProps {
  placeholder?: string;
  icon?: MBIconV2Name;
  files: FileUpload[];
  error?: boolean;
  mode?: 'lean' | 'full';
  isReadOnly?: boolean;
  handleDrop: (files: File[]) => void;
  showFilePicker: () => void;
  uploading: boolean;
}

export function FileInput({
  files,
  placeholder,
  icon,
  error,
  acceptedContentTypes,
  onFileRemoved,
  handleDrop,
  showFilePicker,
  uploading,
  mode = 'full',
  isReadOnly = false,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const responsiveForms = useResponsiveFormContext();
  const dropTargetStyle = mode === 'full' ? styles.fullDropTarget : styles.leanDropTarget;

  const images = useMemo(() => files.filter(isImage), [files]);
  const nonImages = useMemo(() => files.filter(file => !isImage(file)), [files]);
  const displayInitiator = !isReadOnly && (!files.length || mode === 'lean');

  const activeDropTarget = (
    <DropTarget
      style={dropTargetStyle}
      acceptedContentTypes={acceptedContentTypes}
      placeholder={placeholder ?? ''}
      mode={mode}
      state="active"
    />
  );

  return (
    <View padding={1}>
      <MBFileDropListener
        onFilesDropped={handleDrop}
        fill
        overlay={activeDropTarget}
        disabled={isReadOnly}
      >
        {!isEmpty(files) && (
          <View>
            {!isReadOnly && !responsiveForms && (
              <TouchableOpacity
                onPress={showFilePicker}
                disabled={uploading}
                style={[
                  ButtonStyles.button,
                  ButtonStyles.buttonSizeNormal,
                  ButtonStyles.wrap,
                  styles.button,
                  {
                    borderColor: colors.input.borderInactive,
                    backgroundColor: colors.button.tertiary,
                  },
                ]}
              >
                <MBIconV2
                  name={icon}
                  size={24}
                  color={colors.foregroundActive}
                  style={[styles.buttonIcon]}
                />
                <MBTextBody1Bold style={[styles.buttonText, { color: colors.foregroundActive }]}>
                  {placeholder}
                </MBTextBody1Bold>
              </TouchableOpacity>
            )}
            <ImageList files={images} onFileRemoved={onFileRemoved} editable={!isReadOnly} />
            <FileList
              files={nonImages}
              onFileRemoved={onFileRemoved}
              editable={!isReadOnly}
              style={[!isEmpty(images) && { marginTop: grid(0.5) }]}
            />
          </View>
        )}
        {displayInitiator && (
          <MBTouchableOverlay onPress={showFilePicker} fill overlay={activeDropTarget}>
            <DropTarget
              loading={uploading}
              style={dropTargetStyle}
              placeholder={placeholder ?? ''}
              state={error ? 'error' : 'inactive'}
              mode={mode}
              acceptedContentTypes={acceptedContentTypes}
            />
          </MBTouchableOverlay>
        )}
      </MBFileDropListener>
    </View>
  );
}

const styles = StyleSheet.create({
  leanContainer: {
    maxWidth: '60%',
    paddingBottom: grid(1),
  },
  leanDropTarget: {
    flexDirection: 'row',
  },
  fullDropTarget: {
    borderRadius: 8,
    borderStyle: 'dashed',
    borderWidth: 2,
  },
  fullPreview: { paddingHorizontal: grid(3) },
  leanPreview: {
    maxWidth: '60%',
    minHeight: 100,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 'auto',
    minHeight: 0,
    paddingVertical: grid(1),
    paddingHorizontal: grid(1),
    marginBottom: grid(1.5),
    width: '100%',
    justifyContent: 'center',
  },
  buttonIcon: {
    marginRight: grid(1),
    flexShrink: 0,
  },
  buttonText: {
    flexShrink: 1,
  },
});
